import type { StoryModel } from 'types/story';
import type { ClientThunkAction } from 'client/reducers';
import { getResponseErrorMsg } from 'common/resources/api-response';
import { getAssetURL, replaceUrlHostname } from 'utils/assets';
import { StoryFacade } from 'utils/facades/story-facade';
import { GET_STORY_SPREADSHEET_VALUES } from 'client/constants/actions';

export interface SpreadsheetValuesMap {
	[rangeId: string]: {
		range: string;
		majorDimension: 'ROWS';
		values: [string[]];
		type: 'single_cell';
	};
}

async function fetchSpreadsheetValues(url: string): Promise<SpreadsheetValuesMap | null> {
	try {
		const res = await fetch(url);
		return await res.json();
	} catch (e) {
		return {};
	}
}

export type ActionType =
	| { type: typeof GET_STORY_SPREADSHEET_VALUES.FULFILLED; payload: SpreadsheetValuesMap }
	| { type: typeof GET_STORY_SPREADSHEET_VALUES.REJECTED; payload: string };

type ActionResultType = {
	success: boolean;
	error?: string;
	results?: SpreadsheetValuesMap;
};

export function getStorySpreadsheetValuesMap(story: StoryModel): ClientThunkAction<ActionType, ActionResultType> {
	return async dispatch => {
		try {
			const files = new StoryFacade(story).settings.integrations?.spreadsheet?.files;
			const payload: { data: SpreadsheetValuesMap } = { data: {} };

			if (Array.isArray(files) && files.length > 0) {
				await Promise.all(
					files?.map(async file => {
						const assetURL = getAssetURL(file, true);
						const url = replaceUrlHostname(assetURL, story.storycardsDomain);
						const data = await fetchSpreadsheetValues(url);
						Object.assign(payload.data, data);
					})
				);
			}

			dispatch({ type: GET_STORY_SPREADSHEET_VALUES.FULFILLED, payload: payload.data });
			return { success: true, result: payload.data };
		} catch (error) {
			const msg: string = getResponseErrorMsg(error as IBackendResponseError) ?? (error as Error).message;
			dispatch({ type: GET_STORY_SPREADSHEET_VALUES.REJECTED, payload: msg });
			return { success: false, error: msg };
		}
	};
}
